import React, { useEffect, useState } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { Button, Table, message, Spin, Card, Tag } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import request from "helper/request";
import RoutesHeader from "modules/client/routes/components/header/RoutesHeader";
import { shortId } from "helper/util";
import "./ToteDetailPage.scss";

const STATUS_CONFIG = {
  pending: { color: "blue", label: "Pending" },
  submitting: { color: "orange", label: "Processing" },
  submitted: { color: "cyan", label: "Submitted" },
  route_created: { color: "green", label: "Route Created" },
  open: { color: "blue", label: "Open" },
  closed: { color: "purple", label: "Closed" },
  pickedup: { color: "cyan", label: "Picked Up" },
  unassigned: { color: "default", label: "Unassigned" },
  assigned: { color: "green", label: "Assigned" },
  pickup_arrived: { color: "orange", label: "Pickup Arrived" },
  collected: { color: "geekblue", label: "Collected" },
  dropoff_arrived: { color: "orange", label: "Dropoff Arrived" },
  completed: { color: "green", label: "Completed" },
  failed: { color: "red", label: "Failed" }
};

const ROUTE_STATUS_CONFIG = {
  pending: { color: "blue", label: "Pending" },
  accepted: { color: "cyan", label: "Accepted" },
  "in-progress": { color: "orange", label: "In Progress" },
  canceled: { color: "default", label: "Canceled" },
  failed: { color: "red", label: "Failed" },
  completed: { color: "green", label: "Completed" }
};

const StatusTag = ({ status, config }) => {
  const cfg = config[status] || { color: "default", label: status || "-" };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
};

const ToteDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [tote, setTote] = useState(null);
  const [loading, setLoading] = useState(false);

  const breadCrumbs = [
    { text: "SCAN 2 PACK", onClick: () => history.push("/scan2pack") },
    { text: `TOTE ${shortId(id)}` }
  ];

  useEffect(() => {
    fetchTote();
    // eslint-disable-next-line
  }, [id]);

  const fetchTote = async () => {
    setLoading(true);
    try {
      const response = await request.get(`/routeplan/${id}`);
      setTote(response);
    } catch (error) {
      console.error("Error fetching tote:", error);
      message.error("Failed to load tote details");
    } finally {
      setLoading(false);
    }
  };

  const packerName = (() => {
    const driver = tote?.driver;
    if (!driver) return "-";
    const name = [driver.firstName, driver.lastName].filter(Boolean).join(" ");
    return name || driver.email || "-";
  })();

  const packageColumns = [
    {
      title: "Package ID",
      dataIndex: "id",
      key: "id",
      width: 120
    },
    {
      title: "Barcode",
      dataIndex: "barcodeId",
      key: "barcodeId"
    }
  ];

  if (loading) {
    return (
      <div className="tote-detail-page">
        <RoutesHeader breadCrumbs={breadCrumbs} />
        <div className="content-wrapper" style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!tote) {
    return (
      <div className="tote-detail-page">
        <RoutesHeader breadCrumbs={breadCrumbs} />
        <div className="content-wrapper">
          <p>Tote not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tote-detail-page">
      <RoutesHeader breadCrumbs={breadCrumbs} />
      <div className="content-wrapper">
        <div className="header-section">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/scan2pack")}
            style={{ marginBottom: "16px" }}
          >
            Back to Scan 2 Pack
          </Button>
          <h2 className="page-title">Tote: {tote.toteId || shortId(id)}</h2>
        </div>

        <Card className="info-card" style={{ marginBottom: "24px" }}>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Tote ID:</span>
              <span className="value">{tote.toteId || "-"}</span>
            </div>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className="value">
                <StatusTag status={tote.status} config={STATUS_CONFIG} />
              </span>
            </div>
            <div className="info-item">
              <span className="label">Packer Name:</span>
              <span className="value">{packerName}</span>
            </div>
            <div className="info-item">
              <span className="label">Pickup Address:</span>
              <span className="value">{tote.totePickup || "-"}</span>
            </div>
            <div className="info-item">
              <span className="label">Delivery Address:</span>
              <span className="value">{tote.toteDelivery || "-"}</span>
            </div>
            <div className="info-item">
              <span className="label">Total Packages:</span>
              <span className="value">{tote.paquetes?.length || 0}</span>
            </div>
            <div className="info-item">
              <span className="label">Created At:</span>
              <span className="value">
                {tote.createdAt ? new Date(tote.createdAt).toLocaleString() : "-"}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Route:</span>
              <span className="value">
                {tote.routeId ? (
                  <>
                    <Link to={`/routes/${tote.routeId}`}>{shortId(tote.routeId)}</Link>{" "}
                    <StatusTag status={tote.route?.status} config={ROUTE_STATUS_CONFIG} />
                  </>
                ) : (
                  "Not yet processed into a route"
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Stop:</span>
              <span className="value">
                {tote.routeId && tote.jobId ? (
                  <Link to={`/routes/${tote.routeId}/${tote.jobId}`}>
                    {shortId(tote.jobId)}
                  </Link>
                ) : (
                  "-"
                )}
              </span>
            </div>
          </div>
        </Card>

        <h3>Packages ({tote.paquetes?.length || 0})</h3>
        <Table
          dataSource={tote.paquetes || []}
          columns={packageColumns}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} packages`
          }}
        />
      </div>
    </div>
  );
};

export default ToteDetailPage;
